import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/_promo/components/Loader';
import { useButtonAction } from '@/modules/promo';
import { CopyCode } from '../CopyCode';
import { Terms } from '../Terms';

import { ScDialog } from './styled';

interface DialogProps {
  handleSpin?: () => void;
  translation: string;
  spin: number;
  isButtonDisabled: boolean;
  link?: string;
  isLongOffer?: boolean;
  afterRegistrationRedirect?: string;
  bonusCode?: string;
}

interface IDialog {
  title?: string;
  subTitle: string;
  main: string;
  code?: string;
  button: string;
  link?: string;
  terms?: string[];
}

export const Dialog = ({
  handleSpin,
  translation,
  spin,
  isButtonDisabled,
  isLongOffer,
  bonusCode,
  afterRegistrationRedirect,
}: DialogProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(translation);
  const [modalContent, setModalContent] = useState<IDialog | null>(null);
  const [isHasTerms, setIsHasTerms] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { buttonAction, isLoading } = useButtonAction(
    afterRegistrationRedirect
  );

  const handleScroll = () => {
    dialogRef.current?.scrollTo({ top: 450, behavior: 'smooth' });
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      setModalContent(
        (t('modalContent', { returnObjects: true }) as IDialog[])[spin - 1]
      );
    }, 1000);

    return () => clearTimeout(timeOutId);
  }, [spin, t]);

  useEffect(() => {
    if (!modalContent?.terms) return;
    setIsHasTerms(true);
  }, [modalContent]);

  return (
    <ScDialog
      disabled={isLoading}
      isHasTerms={isHasTerms}
      isLongOffer={isLongOffer}
      ref={dialogRef}
    >
      {modalContent &&
        Object.keys(modalContent).map((key) => {
          if (key === 'link') {
            return (
              <button className="link-button" {...buttonAction} key={key}>
                {isLoading ? <Loader /> : modalContent.link}
              </button>
            );
          }

          if (key === 'button') {
            return (
              <button
                className={'dialog__button'}
                dangerouslySetInnerHTML={{
                  __html: modalContent?.button,
                }}
                disabled={isButtonDisabled}
                key={key}
                onClick={handleSpin}
              />
            );
          }

          if (key === 'code') {
            return (
              <CopyCode
                code={bonusCode || (modalContent?.code as string)}
                key={key}
                translation={translation}
              />
            );
          }

          if (key === 'terms') {
            return (
              <Terms
                handleScroll={handleScroll}
                key={key}
                terms={modalContent.terms as string[]}
                translation={translation}
              />
            );
          }

          return (
            <>
              {modalContent[key as keyof IDialog] && (
                <div
                  className={`dialog__${key} ${language}`}
                  dangerouslySetInnerHTML={{
                    __html: modalContent[key as keyof IDialog] as string,
                  }}
                  key={key}
                />
              )}
            </>
          );
        })}
    </ScDialog>
  );
};
